import {Box, Button, CircularProgress, Divider} from "@material-ui/core";
import {useCurrentPublicShow} from "hooks";
import React, {useMemo, useRef, useState} from "react";
import GlossaryCategories from "view/apps/Organiser/pages/Show/Glossary/GlossaryCategories";
import GlossaryTerms from "view/apps/Organiser/pages/Show/Glossary/GlossaryTerms";
import EmptyAlert from "view/components/EmptyAlert";
import FfairPaper from "view/components/FfairPaper";
import SearchInput from "view/components/SearchInput";
import {useTranslate} from "@tolgee/react";
import clsx from "clsx";
import BackToTopButton from "view/components/Layout/BackToTopButton";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import {UserGlossaryRes} from "hooks/show/useGlossary";
import {ICategory, LocalMapType, Roles} from "@evennt-tech/api-v2-core";
import AssignTermPopupToBuildTypeModal from "view/apps/Organiser/pages/Show/Glossary/components/AssignTermPopupToBuildTypeModal";
import {useLocalMapContext} from "view/context/LocalMapContext";
import {defaultLocalMap, makeLowerCase} from "tools/show/localMap";
import {useGlossarySearchQuery} from "store/glossary/glossary.store.selectors";
import FlexBox from "view/components/Layout/FlexBox";
import useStyles from "./styles";

type Props = {
  showId?: string;
  showGlossary: UserGlossaryRes;
  readonly?: boolean;
  withSearch?: boolean;
  onAddFirstCategory?: () => void;
  onAddCategory?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  selectedCategory?: ICategory | null;
  setSelectedCategory: (category: ICategory | null) => void;
  setScrollToTermId: (termId: string | undefined) => void;
  scrollToTerm: () => void;
  selectedTerm: (category: ICategory, termId: string) => void;
  isPrintView?: boolean;
};

const Glossary: React.FC<Props> = ({
  readonly,
  showId,
  showGlossary,
  withSearch,
  onAddFirstCategory,
  onAddCategory,
  selectedCategory,
  setScrollToTermId,
  setSelectedCategory,
  scrollToTerm,
  selectedTerm,
  isPrintView,
}) => {
  const classes = useStyles();
  const {role} = useSelector(selectUser);
  const {t} = useTranslate();
  const {organiser, show: showLabel} = useLocalMapContext();
  const termsListRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState<string>("");
  const [popupTermId, setPopupTermId] = useState<string>("");

  const [show, loading] = useCurrentPublicShow({showId});
  const searchQuery = useGlossarySearchQuery();

  const filteredTerms = useMemo(() => {
    if (!searchValue) return showGlossary.terms;

    return showGlossary.terms.filter(({title}) =>
      title.toLocaleLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, showGlossary.terms]);

  const popupTerm = useMemo(() => {
    if (!popupTermId) {
      return null;
    }
    return showGlossary.terms.find((t) => t.id === popupTermId) ?? null;
  }, [popupTermId, showGlossary.terms]);

  /*
   * The spinner belongs to the first load only.
   *
   * `useCurrentPublicShow` returns react-query's `isFetching`, which is also
   * true for a *background* refresh of data already on screen — and one of
   * those starts whenever a new subscriber to that query mounts while the
   * cached copy is past its 5s `staleTime`. Returning a spinner then replaced
   * this whole tree for a render, unmounting `GlossaryTerms` and silently
   * discarding the section form the organiser had just opened: the pencil
   * "blinked" and did nothing, and the second click — with the query now fresh
   * — worked. So a refresh with data in hand renders nothing special.
   */
  if (!show) {
    return loading ? <CircularProgress size={24} /> : null;
  }

  const OrganiserNoGlossaryAlert = () => {
    const alertAction = (
      <Button onClick={onAddFirstCategory} color="primary">
        Add category
      </Button>
    );

    return (
      <EmptyAlert action={!readonly && alertAction}>
        You’re just getting started! Add your first category.
      </EmptyAlert>
    );
  };

  const ExhibitorNoGlossaryAlert = () => {
    const isOrganiserLocalized = organiser !== defaultLocalMap.organiser;
    const showText = makeLowerCase(showLabel, LocalMapType.Show);
    return (
      <EmptyAlert>
        The{" "}
        <span
          style={{
            fontWeight: isOrganiserLocalized ? 500 : undefined,
            textTransform: isOrganiserLocalized ? "capitalize" : undefined,
          }}
        >
          {organiser}
        </span>{" "}
        of this {showText} has not created the {showText} info yet. Check back
        later.
      </EmptyAlert>
    );
  };

  const NoGlossaryAlert = () => {
    if (onAddFirstCategory) {
      return <OrganiserNoGlossaryAlert />;
    }
    return <ExhibitorNoGlossaryAlert />;
  };

  if (
    !showGlossary ||
    !showGlossary.glossary ||
    !showGlossary?.categories?.length
  ) {
    return <NoGlossaryAlert />;
  }

  const scrollTermsListToTop = () => {
    if (!termsListRef.current) {
      return;
    }
    termsListRef.current.scrollTo(0, 0);
  };

  const backToTopClassname =
    role === "exhibitor" ? classes.exhibitorBackToTop : undefined;

  return (
    <>
      {withSearch && (
        <SearchInput
          onChange={(value) => setSearchValue(value)}
          placeholder={
            role === Roles.EXHIBITOR
              ? t({
                  key: "common.search_placeholder",
                })
              : undefined
          }
        />
      )}
      <FfairPaper className={clsx(classes.glossary, classes.contrast)}>
        {filteredTerms.length === 0 && searchValue ? (
          <Box p={2}>
            {role === Roles.EXHIBITOR
              ? t({
                  key: "common.no_results_found",
                })
              : "No results found"}
          </Box>
        ) : (
          <Box display="flex" style={{width: "100%"}}>
            <FlexBox className={classes.noPrint}>
              <GlossaryCategories
                onAddCategory={onAddCategory}
                showGlossary={showGlossary}
                includeAllCategory={
                  (showGlossary.glossary?.includeAllCategory && !searchQuery) ||
                  false
                }
                categories={showGlossary.categories}
                selectedCategory={selectedCategory}
                readonly={readonly}
                onSelect={(category) => {
                  setSelectedCategory(category);
                  scrollTermsListToTop();
                  setScrollToTermId(undefined);
                }}
              />
              <Divider orientation="vertical" flexItem />
            </FlexBox>
            {showGlossary.glossary && (
              <GlossaryTerms
                selectedTerm={selectedTerm}
                showGlossary={showGlossary}
                scrollToTerm={scrollToTerm}
                termsListRef={termsListRef}
                showId={show.id}
                searchValue={searchValue}
                glossary={showGlossary.glossary}
                terms={filteredTerms}
                categories={showGlossary.categories}
                readonly={readonly}
                selectedCategory={selectedCategory}
                setScrollToTermId={setScrollToTermId}
                setSelectedCategory={setSelectedCategory}
                onSetPopupTermId={(termId) => setPopupTermId(termId)}
                isPrintView={isPrintView}
              />
            )}

            <Box position="relative">
              <BackToTopButton
                className={backToTopClassname}
                anchor={termsListRef}
              />
            </Box>
          </Box>
        )}
      </FfairPaper>
      {popupTerm && (
        <AssignTermPopupToBuildTypeModal
          onClose={() => setPopupTermId("")}
          popupTerm={popupTerm}
          terms={showGlossary.allTerms}
        />
      )}
    </>
  );
};

Glossary.defaultProps = {
  readonly: false,
};

export default Glossary;
